// Libraries
import moment from 'moment'
import {
  CognitoUserPool,
  CognitoUser,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

// Services
import api from '../../api'
import store from '../index'
import { EventBus } from '../../services/eventBusService.js'

// Utilities
import analytics from '../../utility/analyticsUtil'
import DeviceUtil from '../../utility/deviceUtil'
import Formatter from '../../utility/formatterUtil'
import isEmpty from '../../utility/isEmpty'
import logger from '../../utility/loggerUtil'
import messaging from '../../utility/messagingUtil'
import mobilePhoneVerifyUtil from '../../utility/mobilePhoneVerifyUtil'
import Preference from '../../utility/preferenceUtil'
import router from '../../utility/routerUtil'
import session from '../../utility/sessionUtil'
import sessionRecord from '../../utility/sessionRecordUtil'
import wait from '../../utility/waitUtil'

// Validators
import { phone } from '../../validators/phone'

// Constants
import { AnalyticsConstants, CognitoConstants, KeyConstants } from '../../constants'

export default {
  async authenticate (context, data) {
    try {
      if (!data.isEmailVerification) {
        context.commit('display/setForceWait', true, { root: true })
        wait.waitStart('session/authenticate')
      }

      logger.info('Before login attempt in store...')

      let user
      let isGuestPayOnlyUser
      if (data.profileId && (data.flow || data.signature)) {
        let authResponse
        if (data.flow) {
          authResponse = await api.user.deviceKeyAuth(data.flow, data.profileId)
        } else if (data.signature) {
          authResponse = await api.user.signatureAuth(data.profileId, data.signature, data.expiration, data.path, data.isGuestPayment, data.applicationId)
        } else {
          throw new Error('No recognized authentication parameters.')
        }
        authResponse = Object.assign(authResponse, { profileId: data.profileId })
        user = await context.dispatch('createSessionFromServerAuth', authResponse)
        isGuestPayOnlyUser = authResponse.isGuestPayOnlyUser // this will only be present on the signatureAuth response
      } else if (data.username && data.authResult) {
        user = await api.user.loginFromAuthResult(data.authResult, data.username)
      } else {
        const usernamePasswordResponse = await api.user.login(data.username, data.password, data.rememberMe, context.state.fromLegacyNcr)
        user = usernamePasswordResponse.user

        router.clearLockedFlows()

        await api.user.deviceKeySignout().catch(e => { // clear the locked flow on normal login, let them continue if error
          logger.error('Error on device key signout')
          logger.debug(e)
        })
      }

      if (context.rootState.payment.guestCard && context.rootState.payment.saveGuestCard) {
        context.commit('payment/reset', undefined, { root: true })
      }

      const { email, emailVerified, phone, phoneVerified, username } = await getContactInfo(context)

      if (!context.getters.usernameIsEmail) { // If logging in with a verified phone number, customer does not need to go through text verification
        context.commit('applicationCustomer/updatePhoneVerified', {
          phoneNumberVerified: true
        }, { root: true })
      }

      context.commit('authenticated', { user, username, email, emailVerified, phone, phoneVerified, isGuestPayOnlyUser })
      await context.dispatch('onLogin', {
        processCognito: true,
        refreshIdentity: true,
        doLoginEvent: true,
        loginType: username.includes('@') ? AnalyticsConstants.method.loginType.email : AnalyticsConstants.method.loginType.phone,
        loginMedium: AnalyticsConstants.medium.login.password
      })
    } catch (e) {
      const responseMessage = !isEmpty(e.message) && e.message.toLowerCase().includes('request failed with status code') ? undefined : e.message
      const responseError = !isEmpty(e.response) && !isEmpty(e.response.data) ? e.response.data.error : undefined
      const err = new Error(e.errorCode || responseMessage || responseError)
      err.detail = e.message
      throw err
    } finally {
      if (!data.isEmailVerification) {
        context.commit('display/setForceWait', false, { root: true })
        wait.waitEnd('session/authenticate')
      }
    }
  },

  async register (context, data) {
    try {
      let guestCardIds

      if (context.rootState.payment.guestCard && context.rootState.payment.saveGuestCard) {
        guestCardIds = context.rootState.payment.paymentCardIds
      }

      const user = await api.user.register(
        data.username,
        data.password,
        guestCardIds,
        data.lastFourOfSsn,
        data.referralId,
        data.dateOfBirth,
        data.isReferralOnly
      )

      context.commit('unverified', { user, username: data.username, isReferralOnly: data.isReferralOnly })

      return user
    } catch (e) {
      logger.error('Error on registration')
      logger.debug(e)
      throw e
    }
  },

  async updateUsername (context, data) {
    try {
      const user = await api.user.updateUsername(data.oldUsername, data.newUsername, data.password)
      context.commit('unverified', { user, username: data.newUsername })
      return user
    } catch (e) {
      logger.error('Error updating username')
      logger.debug(e)
      throw e
    }
  },

  async verify (context, data) {
    try {
      const user = await api.user.verify(data.username, data.code, data.isEmail)
      context.commit('authenticated', { user, username: data.username, emailVerified: data.isEmail, phoneVerified: !data.isEmail })

      if (!data.isEmail) {
        context.commit('applicationCustomer/updatePhoneVerified', {
          phoneNumberVerified: true
        }, { root: true })
      }
    } catch (e) {
      logger.error('Error on session/verify')
      logger.debug(e)
      throw e
    }
  },

  async resendVerificationCode (context, data) {
    try {
      await api.user.resendVerificationCode(data.username)
    } catch (e) {
      logger.error('Error on resend verification')
      logger.debug(e)
      throw new Error(e)
    }
  },

  async signout (context, data) {
    try {
      wait.waitStart('session/signout')

      context.commit('setSignOutInProgress', true) // Prevent loop of signout calls when auth tokens expire

      if (!isEmpty(session.getAccessToken())) {
        await api.user.deviceKeySignout().catch(e => { // clear the locked flow on normal login, let them continue if error
          logger.error('Error on device key signout')
          logger.error(e)
        })
      }

      await api.user.signout()
      router.clearLockedFlows()

      context.commit('signout')
      context.commit('customer/clearCustomerData', null, { root: true })

      if (isEmpty(data) || isEmpty(data.skipPaymentReset) || !data.skipPaymentReset) {
        context.commit('payment/clearPaymentData', null, { root: true })
      }

      context.commit('cards/reset', null, { root: true })
      context.commit('config/reset', null, { root: true })
      context.commit('loans/reset', null, { root: true })
      context.commit('appElement/reset', null, { root: true })
      context.commit('store/reset', null, { root: true })
      context.commit('brands/reset', null, { root: true })
      context.commit('referral/reset', null, { root: true })
      context.commit('offers/reset', null, { root: true })
      context.commit('display/closeSidebar', null, { root: true })
      context.commit('origination/reset', undefined, { root: true })
      EventBus.$emit('signout')
      store.cache.clear()

      context.commit('setSignOutInProgress', false)
    } catch (e) {
      throw new Error(e)
    } finally {
      wait.waitEnd('session/signout')
    }
  },

  /**
   * Gets the global service status for maintenance mode
   */
  async getServiceStatus (context) {
    try {
      const status = await api.config.getServiceStatus()
      context.commit('updateServiceStatus', status)
      return status
    } catch (e) {
      logger.error('Error on session/getServiceStatus')
      logger.debug(e)
      throw e
    }
  },

  async processTrustedDevice (context, data) {
    const trustedDevice = await context.dispatch('checkTrustedDeviceStatus')

    const { nextRoute, enableFlowLock } = data

    let trustVerifyPage

    if (isEmpty(trustedDevice) || !trustedDevice.isTrusted) {
      if (enableFlowLock && !isEmpty(router.getLockedFlows())) {
        router.addLockedFlow('TrustedDevice')
      }

      const username = !isEmpty(trustedDevice.contactInfo) ? trustedDevice.contactInfo : context.getters.getUsername
      const isUsernamePhone = !isEmpty(username) && username.includes('@') === false
      const queryString = Object.entries(nextRoute.query).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')
      const fullPath = `${nextRoute.path}?${queryString}`

      logger.debug(`Trusted device redirect path: ${fullPath}`)

      if (isUsernamePhone) {
        trustVerifyPage = { name: 'TrustedDeviceVerificationMethod', query: { phoneNumber: username, redirect: fullPath, isEmailPrimary: false } }
      } else if (trustedDevice.hasError) {
        if (!context.getters['customer/isTlxeUser'] && !context.getters['customer/isNonCustomer']) { // Likely failed due to no customer linked, send them to link account
          trustVerifyPage = { name: 'LinkAccount' }
        } else {
          trustVerifyPage = { name: 'TrustedDeviceWarning', query: { redirect: fullPath, hasDeviceVerificationError: true } }
        }
      } else {
        trustVerifyPage = { name: 'TrustedDeviceWarning', query: { redirect: fullPath, contactInfo: username } }
      }
    } else {
      store.commit('session/setDeviceIsTrusted', true)
    }

    return trustVerifyPage
  },

  /**
   * Checks to see if the current device is trusted or not, using the Cognito device key.
   * @return {Promise<Boolean>} true if the device is trusted, false otherwise.
   */
  async checkTrustedDeviceStatus (context, skipWaitIndicator = false) {
    try {
      if (!skipWaitIndicator) {
        wait.waitStart('session/checkTrustedDeviceStatus')
      }

      const deviceKey = await context.dispatch('getCurrentDeviceKey')
        .catch(e => {
          logger.error('Error getting device key')
          logger.debug(e)
        })

      if (isEmpty(deviceKey)) {
        return {
          isTrusted: false
        }
      }

      const isEmail = !phone(context.getters.getUsername)
      const response = await api.user.getTrustedDeviceStatus(deviceKey, isEmail)

      return {
        isTrusted: response.isTrusted === true,
        contactInfo: response.contactInfo,
        deviceKey,
        hasError: response.hasError
      }
    } catch (e) {
      logger.error('Error checking trusted device')
      logger.debug(e)
      throw e
    } finally {
      if (!skipWaitIndicator) {
        wait.waitEnd('session/checkTrustedDeviceStatus')
      }
    }
  },

  /**
   * Sends a new trusted device code to the Cognito account for a given device.
   */
  async sendTrustedDeviceCode (context, data) {
    const deviceKey = await context.dispatch('getCurrentDeviceKey')
    const isPhone = phone(context.getters.getUsername)
    const isVoice = !isEmpty(data) ? data.isVoice : false

    await api.user.sendTrustedDeviceCode(deviceKey, !isPhone, isVoice)
  },

  /**
   * Sends a new trusted device code to the Cognito account for a given device.
   */
  async confirmTrustedDeviceCode (context, data) {
    let deviceKey = data.deviceKey

    if (isEmpty(deviceKey)) {
      deviceKey = await context.dispatch('getCurrentDeviceKey')
    }

    await api.user.confirmTrustedDeviceCode(deviceKey, data.code, data.profileId)
    context.commit('setDeviceIsTrusted', true)
  },

  /**
   * Sends a new user update code to the Cognito account for a given alias.
   */
  async confirmUserUpdateCode (context, data) {
    await api.user.confirmUserUpdateCode(data.newAlias, data.code)

    if (store.cache.has('customer/getCustomerInfo')) {
      store.cache.delete('customer/getCustomerInfo')
    }
  },

  isOverSessionTimeout () {
    const minutesDifference = moment().diff(store.state.session.sessionTimeoutStart, 'minutes')

    logger.debug(`Session timeout length (minutes): ${minutesDifference}`)

    return minutesDifference >= store.state.session.sessionTimeoutLimit
  },

  async getCurrentDeviceKey () {
    let deviceKey = window.localStorage.getItem(KeyConstants.deviceKey)

    if (isEmpty(deviceKey)) {
      deviceKey = session.getDeviceKey() // Issue with Cognito SDK prevents device key from being accessed when doing custom auth on the first login
      if (isEmpty(deviceKey)) {
        deviceKey = (await api.user.getCurrentDevice()).Device.DeviceKey
      }
    }

    if (isEmpty(deviceKey)) { // If it is still empty, throw
      throw new Error('MissingDeviceKey')
    }

    window.localStorage.setItem(KeyConstants.deviceKey, deviceKey)

    return deviceKey
  },

  async setDeviceKey (context, deviceKey) {
    logger.debug(`Setting new device key: ${deviceKey}`)

    window.localStorage.setItem(KeyConstants.deviceKey, deviceKey)
  },

  async createSessionFromServerAuth (context, data) {
    const { profileId, accessToken, deviceKey, deviceGroupKey, idToken, refreshToken } = data

    logger.debug('Refresh client SDK with tokens and device key')

    const cognitoUserPool = new CognitoUserPool({
      UserPoolId: context.state.servicesConfig.userPoolId, // Your user pool id here
      ClientId: context.state.servicesConfig.clientId, // Your client id here
      Storage: window.sessionStorage
    })

    const AccessToken = new CognitoAccessToken({ AccessToken: accessToken })
    const IdToken = new CognitoIdToken({ IdToken: idToken })
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken })

    const sessionData = {
      IdToken,
      AccessToken,
      RefreshToken
    }

    const userSession = new CognitoUserSession(sessionData)

    const userData = {
      Username: profileId,
      Pool: cognitoUserPool,
      Storage: window.sessionStorage
    }

    const cognitoUser = new CognitoUser(userData)

    cognitoUser.setSignInUserSession(userSession)

    // The SDK only persists LastAuthUser/tokens via cacheTokens(). Without it, refreshSession() can't find the cached device key and fails with NotAuthorizedException.
    cognitoUser.cacheTokens()

    // This seems to be the only way to fill in these properties when not using the client SDK authenticate method
    cognitoUser.deviceKey = deviceKey
    cognitoUser.deviceGroupKey = deviceGroupKey

    cognitoUser.cacheDeviceKeyAndPassword()
    cognitoUser.getCachedDeviceKeyAndPassword()

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => { // You must run this to verify that session (internally)
        if (err) {
          logger.error('Error on get session')
          logger.debug(err)
          reject(err)
        }

        if (session.isValid()) {
          logger.debug(session)
          logger.debug(`access token: ${session.getAccessToken().getJwtToken()}`)
          logger.debug(`id token: ${session.getIdToken().getJwtToken()}`)

          resolve(cognitoUser)
        } else {
          logger.error('Invalid session')
          logger.debug(session)
          reject(new Error('Invalid session')) // TODO: Handle this error appropriately
        }
      })
    })
  },

  async getPasswordPolicy (context) {
    try {
      const passwordPolicy = await api.user.getPasswordPolicy()
      context.commit('setPasswordPolicy', passwordPolicy)
    } catch (e) {
      logger.error('Error getting password policy')
      logger.debug(e)
      throw e
    }
  },

  async getConfigSettings (context) {
    const configSettings = await api.config.getConfigSettings().catch(e => {
      logger.error('Error getting config settings, trying again...')
      logger.debug(e)
      return api.config.getConfigSettings()
    })

    logger.info('Got config settings')

    context.commit('updateConfigSettings', configSettings.appConfig)
    context.commit('applicationSession/updateConfigSettings', configSettings.appConfig, { root: true })
  },

  async setShowAllLoans () {
    try {
      const attributes = await api.user.getAttributes()
      const paidOffAttribute = attributes.filter(attr => attr.getName() === CognitoConstants.showPaidOffAttribute)

      if (paidOffAttribute && !isEmpty(paidOffAttribute.value)) {
        store.commit('display/setShowAllLoans', paidOffAttribute.value, { root: true })
      }
    } catch (e) {
      logger.error('Unable to set `show all loans` attribute')
      logger.debug(e)
    }
  },

  async onLogin (context, data) {
    const { processCognito, refreshIdentity, doLoginEvent, loginType, loginMedium } = data
    if (processCognito) {
      await context.dispatch('setShowAllLoans')
      await analytics.setUserId(context.getters.getCognitoId)
      analytics.setUserProperty(AnalyticsConstants.propName.usernameTypeUser, data.loginType)
      sessionRecord.setUserProperty(data.loginType, AnalyticsConstants.propName.usernameTypeUser)
    }

    if (refreshIdentity) {
      await messaging.onLogin()
      analytics.sendCustomerAnalytics()
      sessionRecord.updateIdentity()
    }

    if (doLoginEvent) {
      analytics.logEvent(AnalyticsConstants.eventName.login, { method: loginType, medium: loginMedium, login_success: true }, { logMarketingEvent: true }) // This should go below the user property setting above to ensure new events to pick up the new user properties
    }
  },

  setAwaitingSignature (context, awaitingSignature) {
    try {
      context.commit('updateConfigSettings', { awaitingSignature })
    } catch (e) {
      logger.error('Error on session/setAwaitingSignature')
      logger.debug(e)
    }
  },

  async proveMobileAuthVerifyAttempt (context, data) {
    try {
      const { phoneNumber, forceInstantLink } = data
      if (!forceInstantLink && DeviceUtil.getIsMobileDevice()) {
        const mobileAuthResponse = await mobilePhoneVerifyUtil.authenticate(Formatter.stripPhoneNumber(phoneNumber))
          .catch(err => {
            logger.error('Error on mobilePhoneVerifyUtil.authenticate')
            logger.debug(err)
          })

        return mobileAuthResponse
      }

      const sessionId = await Preference.get(KeyConstants.sessionId, '')
      const instantLinkResponse = await api.user.initiatePhoneVerification(
        'instant',
        Formatter.stripPhoneNumber(phoneNumber),
        sessionId
      )

      return instantLinkResponse
    } catch (e) {
      logger.error('Error on session/proveMobileAuthVerifyAttempt')
      logger.debug(e)
      throw e
    }
  },

  async getInstantLinkVerificationStatus (context, phoneNumber) {
    try {
      const response = await api.user.getInstantLinkStatus(phoneNumber)

      return response
    } catch (e) {
      logger.error('Error on session/getInstantLinkVerificationStatus')
      logger.debug(e)
      throw e
    }
  },

  async confirmPhoneVerifyResult (context, data) {
    try {
      const { type, vfp, userData } = data
      const sessionId = Preference.get(KeyConstants.sessionId, '')
      const response = await api.user.phoneVerifyResult(type, vfp, userData, sessionId)

      return response
    } catch (e) {
      logger.error('Error on session/confirmPhoneVerifyResult')
      logger.debug(e)
      throw e
    }
  }
}

const getContactInfo = async (context) => {
  let username
  const { attributes } = await context.dispatch('customer/getCustomerId', undefined, { root: true })

  const emailAttribute = attributes.find(attr => attr.getName() === CognitoConstants.emailAttribute)
  const phoneAttribute = attributes.find(attr => attr.getName() === CognitoConstants.phoneNumberAttribute)

  const email = !isEmpty(emailAttribute) ? emailAttribute.getValue() : ''
  const phone = !isEmpty(phoneAttribute) ? phoneAttribute.getValue() : ''

  const emailVerifiedAttribute = attributes.find(attr => attr.getName() === CognitoConstants.emailVerifiedAttribute)
  const emailVerified = !isEmpty(emailVerifiedAttribute) && emailVerifiedAttribute.getValue() === 'true'

  const phoneVerifiedAttribute = attributes.find(attr => attr.getName() === CognitoConstants.phoneNumberVerifiedAttribute)
  const phoneVerified = !isEmpty(phoneVerifiedAttribute) && phoneVerifiedAttribute.getValue() === 'true'

  if (!isEmpty(email) && emailVerified) {
    username = email
  } else if (!isEmpty(phone) && phoneVerified) {
    username = phone
  }

  return {
    email,
    emailVerified,
    phone,
    phoneVerified,
    username
  }
}
